import { html, TemplateResult } from 'lit';
import { choose } from 'lit/directives/choose.js';
import { OrderDto, OrderStatus } from '../../../interfaces/index.js';

const renderPending = (orderCode: string) => {
  return html`
    <p>Jetzt kannst du deinen Film einsenden:</p>
    <ol>
      <li>
        <div>
          <strong>Film sicher verpacken:</strong>
          <br />
          Nutze einen Umschlag und unsere Schutzhülle:
          <br />
          <br />
          <a
            href="/downloads/faltanleitung.pdf"
            aria-label="Faltanleitung PDF 21 KB"
            download
          >
            <span class="download-label">
              Faltanleitung
              <span class="download-meta">PDF • 21 KB</span>
            </span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 -960 960 960"
              width="24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M480-320 280-520l56-58 104 104v-326h80v326l104-104
               56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120
               h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"
              ></path>
            </svg>
          </a>
        </div>
      </li>

      <li>
        <div>
          <strong>Auftragsnummer notieren:</strong>
          <br />
          Schreibe deine Auftragsnummer (${orderCode})
          <br />
          auf die Innenseite des Umschlags oder auf unsere Schutzhülle.
        </div>
      </li>

      <li>
        <div>
          <strong>Film versenden:</strong>
          <address>
            <strong>ScanLab</strong><br />
            Schloßgasse 1<br />
            07743 Jena
          </address>
        </div>
      </li>
    </ol>
    <p>Bitte beachte, dass wir nur bereits entwickelte 35mm Filme annehmen.</p>
  `;
};

const renderReceived = () => {
  return html`
    <p>
      Dein Film ist eingegangen.<br />
      Wir beginnen bald mit der Digitalisierung.
    </p>
  `;
};

const renderProcessing = () => {
  return html`
    <p>
      Dein Auftrag wird gerade digitalisiert.<br />
      Bitte habe etwas Geduld, je nach Menge an Bildern und Auftragslage kann
      dies ein paar Tage in Anspruch nehmen.
    </p>
  `;
};

const renderCompleted = (order: OrderDto) => {
  const {
    code,
    createdAt,
    discountedPrice,
    imageCount,
    price,
    updatedAt,
    withDiscount,
  } = order;

  const date = updatedAt || createdAt;
  const year = new Date(date).getFullYear();

  // Kostenübersicht
  const regularPriceDetails = html`
    <td>Gesamtpreis</td>
    <td>${price} €</td>
  `;

  const discountDetails = html`
    <td>Preis mit Rabatt</td>
    <td>
      <span class="visually-hidden">Ursprünglicher Preis</span><s>${price} €</s>
      <span class="visually-hidden">Angebotspreis</span>
      ${discountedPrice} €
    </td>
  `;

  const priceDetails = html`
    <table>
      <caption>
        Kostenübersicht
      </caption>
      <tr>
        ${withDiscount ? discountDetails : regularPriceDetails}
      </tr>
    </table>
  `;

  // Auftragsdetails
  const orderDetails = html`
    <table>
      <caption>
        Auftragsdetails
      </caption>
      <tr>
        <td>Anzahl Bilder</td>
        <td>${imageCount ?? 0}</td>
      </tr>
      <tr>
        <td>Auflösung:</td>
        <td>~ 5500 x 3500</td>
      </tr>
      <tr>
        <td>Dateiformat:</td>
        <td>JPEG</td>
      </tr>
    </table>
  `;

  // Zahlungsinformationen
  const paymentInfo = html`
    <table>
      <caption>
        Zahlungsinformationen
      </caption>
      <tr role="row">
        <td role="cell">Betreff:</td>
        <td role="cell">${year}-${code}</td>
      </tr>
      <tr role="row">
        <td role="cell">IBAN:</td>
        <td role="cell">DE43100110012620018025</td>
      </tr>

      <tr role="row">
        <td role="cell">BIC:</td>
        <td role="cell">COBADEFFXXX</td>
      </tr>
    </table>
  `;

  return html`
    ${priceDetails} ${orderDetails} ${paymentInfo}
    <p>
      Nach dem Zahlungseingang erhältst du auf dieser Seite einen Link zum
      Download deiner Bilder.
    </p>
  `;
};

const renderPaid = (order: OrderDto) => {
  const { downloadLink } = order;
  if (!downloadLink) return html`<p>Download-Link wird generiert...</p>`;

  const downloadSize = 320;

  return html`
    <p>
      Dein Auftrag ist abgeschlossen.<br />
      Viel Spaß mit deinen Bildern!
    </p>
    <br />

    <a download href="${downloadLink}">
      <span class="download-label">
        Bilder herunterladen
        <span class="download-meta">ZIP • ${downloadSize} MB</span>
      </span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 -960 960 960"
        width="24px"
        fill="currentColor"
      >
        <path
          d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"
        />
      </svg>
    </a>
  `;
};

const renderCancelled = () => {
  return html`
    <p>Dein Auftrag wurde abgebrochen. Bei Fragen kontaktiere uns bitte.</p>
  `;
};

export const renderOrderContent = (order: OrderDto): TemplateResult => {
  const { status, code } = order;

  return html`
    ${choose<OrderStatus, TemplateResult>(status, [
      ['pending', () => renderPending(code)],
      ['received', renderReceived],
      ['processing', renderProcessing],
      ['completed', () => renderCompleted(order)],
      ['paid', () => renderPaid(order)],
      ['cancelled', renderCancelled],
    ])}
  `;
};
